import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Clock, Facebook, Instagram, MapPin } from "lucide-react";
import Logo from "./Logo";
import { restaurantInfo } from "../data/mock";
import { useI18n } from "../data/i18n";

const FACEBOOK_URL = "https://www.facebook.com/share/18nvmz6Z2e/?mibextid=wwXIfr";
const INSTAGRAM_URL = "https://www.instagram.com/casabala.restaurant";

const Header = () => {
  const location = useLocation();
  const { t, lang, setLang } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const lastScrollY = useRef(0);

  const navLinks = [
    { key: "nav.home", path: "/" },
    { key: "nav.menu", path: "/menu" },
    { key: "nav.reservation", path: "/reservation" },
    { key: "nav.order", path: "/order" },
    { key: "nav.gallery", path: "/gallery" },
    { key: "nav.events", path: "/events" },
    { key: "nav.about", path: "/about" },
    { key: "nav.contact", path: "/contact" },
  ];

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 30);
      if (y > 10 && y > lastScrollY.current) setHidden(true);
      else if (y < lastScrollY.current || y <= 10) setHidden(false);
      lastScrollY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const LangToggle = ({ compact = false }) => (
    <div className={`lang-toggle ${compact ? "lang-toggle-compact" : ""}`}>
      <button
        onClick={() => setLang("bg")}
        className={`lang-toggle-btn ${lang === "bg" ? "is-active" : ""}`}
        aria-label="Switch to Bulgarian"
      >BG</button>
      <span className="lang-toggle-sep">/</span>
      <button
        onClick={() => setLang("en")}
        className={`lang-toggle-btn ${lang === "en" ? "is-active" : ""}`}
        aria-label="Switch to English"
      >EN</button>
    </div>
  );

  return (
    <>
      <header className={`site-header ${scrolled ? "site-header-scrolled" : ""} ${hidden && !mobileOpen ? "site-header-hidden" : ""}`}>
        {/* Top bar */}
        <div className="hidden lg:flex items-center justify-between px-8 py-2 text-xs text-[#cfc7b8] border-b border-[#1f1f1f]/50">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-[#e4c590]" />
              <span>{t("common.opening_hours")}</span>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <a href={`tel:${restaurantInfo.phone}`} className="flex items-center gap-2 hover:text-[#e4c590] transition-colors">
              <Phone size={14} className="text-[#e4c590]" />
              <span>{restaurantInfo.phone}</span>
            </a>
          </div>
        </div>

        {/* Main nav */}
        <div className="flex items-center justify-between px-6 lg:px-12 py-4">
          <Logo size="md" />

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link ${location.pathname === link.path ? "active" : ""}`}
              >
                {t(link.key)}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <div className="hidden lg:block">
              <LangToggle />
            </div>
            <div className="lg:hidden">
              <LangToggle compact />
            </div>
            <button
              className={`lg:hidden hamburger ${mobileOpen ? "hamburger-open" : ""}`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
            >
              <span></span><span></span><span></span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <div className={`mobile-menu ${mobileOpen ? "mobile-menu-open" : ""}`}>
        <div className="mobile-menu-bg"></div>
        <div className="mobile-menu-inner">
          <div className="mobile-menu-top">
            <Logo size="sm" />
            <div className="flex items-center gap-4">
              <LangToggle compact />
              <button className="hamburger hamburger-open" onClick={() => setMobileOpen(false)} aria-label="Close menu">
                <span></span><span></span><span></span>
              </button>
            </div>
          </div>

          <nav className="mobile-menu-nav">
            {navLinks.map((link, idx) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className={`mobile-menu-link ${location.pathname === link.path ? "is-active" : ""}`}
                style={{ "--i": idx }}
              >
                <span className="mobile-menu-link-text">{t(link.key)}</span>
                <span className="mobile-menu-link-arrow">→</span>
              </Link>
            ))}
          </nav>

          <div className="mobile-menu-bottom">
            <div className="diamond-divider-mob">
              <span className="diamond-mark"></span>
              <span className="diamond-mark opacity-60"></span>
              <span className="diamond-mark"></span>
            </div>
            <div className="mobile-menu-contact">
              <a href={`tel:${restaurantInfo.phone}`} className="mobile-menu-contact-item">
                <Phone size={16} />
                <span>{restaurantInfo.phone}</span>
              </a>
              <div className="mobile-menu-contact-item">
                <MapPin size={16} />
                <span>{t("contact.street")}</span>
              </div>
              <div className="mobile-menu-contact-item">
                <Clock size={16} />
                <span>{t("common.opening_hours")}</span>
              </div>
            </div>
            <div className="mobile-menu-socials">
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" aria-label="CASA BALA във Facebook" className="mobile-menu-social"><Facebook size={16} /></a>
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="CASA BALA в Instagram" className="mobile-menu-social"><Instagram size={16} /></a>
            </div>
            <Link to="/order" onClick={() => setMobileOpen(false)} className="cb-btn cb-btn-gold mobile-menu-cta">
              <span className="cb-btn-text cb-btn-text-1">{t("nav.cta_order")}</span>
              <span className="cb-btn-text cb-btn-text-2" aria-hidden="true">{t("nav.cta_order")}</span>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
